import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Sparkles, BarChart3, Users, MessageSquare, Camera, PenSquare,
  Brain, ShieldCheck, Globe, GraduationCap, Package, Zap,
  ChevronDown, ArrowRight, Star, Check, Menu, X
} from "lucide-react";
import "./Landing.css";

const FEATURES = [
  {
    icon: <BarChart3 size={28} />,
    title: "Smart Dashboard",
    desc: "Real-time sales metrics, team performance, daily tips, and quick actions — everything at a glance.",
    color: "#1B6EF3",
  },
  {
    icon: <Users size={28} />,
    title: "Customer CRM",
    desc: "Manage your customers, track status, and never lose a lead. Full search, filter, and detail views.",
    color: "#8B5CF6",
  },
  {
    icon: <Sparkles size={28} />,
    title: "Follow-Up Autopilot",
    desc: "AI generates personalized SMS follow-ups for each customer based on their interests and history.",
    color: "#10B981",
  },
  {
    icon: <Camera size={28} />,
    title: "AI Room Visualizer",
    desc: "Upload customer room photos, remove furniture backgrounds, and composite new pieces right into the room.",
    color: "#F59E0B",
  },
  {
    icon: <PenSquare size={28} />,
    title: "Create Post & Flyers",
    desc: "Generate social media posts, infographic flyers, and promo materials with AI — ready for Facebook, Instagram & TikTok.",
    color: "#EF4444",
  },
  {
    icon: <Brain size={28} />,
    title: "Furniture Fred AI Chat",
    desc: "Your AI sales copilot. Get instant help with objections, pricing questions, and closing strategies.",
    color: "#6366F1",
  },
  {
    icon: <MessageSquare size={28} />,
    title: "Messages & Easy Estimate",
    desc: "Chat with customers, get AI smart replies, and build line-item estimates with tax, warranty & delivery.",
    color: "#0EA5E9",
  },
  {
    icon: <GraduationCap size={28} />,
    title: "Sales Training",
    desc: "AI-powered role-play scenarios. Practice handling objections and get graded with actionable feedback.",
    color: "#D946EF",
  },
  {
    icon: <Globe size={28} />,
    title: "Language Translator",
    desc: "Real-time translation with speech recognition, pronunciation guides, and cultural notes for diverse customers.",
    color: "#14B8A6",
  },
  {
    icon: <Package size={28} />,
    title: "Product Catalog",
    desc: "Scrape products from any website, manage inventory details, and have everything your team needs at their fingertips.",
    color: "#F97316",
  },
  {
    icon: <ShieldCheck size={28} />,
    title: "Team Management",
    desc: "Admin and manager views, role-based access, and oversight across your entire sales floor.",
    color: "#64748B",
  },
  {
    icon: <Zap size={28} />,
    title: "Instant Setup",
    desc: "No downloads. No installations. Your team is up and running in minutes from any browser.",
    color: "#FBBF24",
  },
];

const FAQS = [
  {
    q: "How quickly can my team start using Showroom AiPro?",
    a: "Immediately. There's nothing to install — your team logs in from any browser on any device. We'll have your store set up and your salespeople productive within minutes.",
  },
  {
    q: "Do my salespeople need to be tech-savvy?",
    a: "Not at all. Showroom AiPro is designed specifically for furniture sales teams. The interface is intuitive, and features like Furniture Fred AI Chat guide your team through every interaction.",
  },
  {
    q: "What does the AI actually do?",
    a: "Our AI handles follow-up messages, social media posts, flyer design, room visualization, sales objection training, customer reply suggestions, estimates, and real-time language translation — all tailored for furniture retail.",
  },
  {
    q: "Can managers see what their team is doing?",
    a: "Yes. Managers and admins get oversight views across all conversations, team performance dashboards, and the ability to manage roles and access for the entire sales floor.",
  },
  {
    q: "Is the $99/month launch price per user or per store?",
    a: "The $99/month launch price is per store — your entire team gets access. This is a limited-time introductory rate before we move to our regular pricing of $249/month.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Absolutely. There are no long-term contracts. You can cancel your subscription at any time with no penalties.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const goToLogin = () => navigate("/login");
  const goToRegister = () => navigate("/register");

  return (
    <div className="landing">
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <div className="landing-logo-icon">
              <Sparkles size={22} />
            </div>
            <span>Showroom <strong>AiPro</strong></span>
          </div>
          <div className={`landing-nav-links ${mobileMenuOpen ? "open" : ""}`}>
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <button className="landing-nav-login" onClick={goToLogin}>Store Login</button>
          </div>
          <button className="landing-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <section className="landing-hero">
        <div className="landing-hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          <div className="hero-orb hero-orb-3" />
          <div className="hero-grid" />
        </div>
        <div className="landing-hero-content">
          <div className="hero-badge">
            <Zap size={14} /> Limited Launch Pricing — Save $150/mo
          </div>
          <h1>
            The AI-Powered Sales Platform<br />
            <span className="hero-gradient-text">Built for Furniture Stores</span>
          </h1>
          <p className="hero-subtitle">
            Give your sales team AI superpowers. Showroom AiPro handles follow-ups, room visualization,
            social media, customer messaging, training, and more — so your team closes more deals.
          </p>
          <div className="hero-cta-row">
            <button className="hero-cta-primary" onClick={goToRegister}>
              Start 7-Day Free Trial <ArrowRight size={18} />
            </button>
            <a href="#features" className="hero-cta-secondary">
              See All Features
            </a>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-number">10+</span>
              <span className="hero-stat-label">AI-Powered Tools</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <span className="hero-stat-number">24/7</span>
              <span className="hero-stat-label">AI Sales Assistant</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <span className="hero-stat-number">0</span>
              <span className="hero-stat-label">Setup Required</span>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-social-proof">
        <div className="social-proof-inner">
          <p>Trusted by forward-thinking furniture retailers</p>
          <div className="social-proof-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={20} fill="#FBBF24" color="#FBBF24" />
            ))}
            <span>Built specifically for the furniture industry</span>
          </div>
        </div>
      </section>

      <section className="landing-features" id="features">
        <div className="landing-section-inner">
          <div className="section-header">
            <span className="section-badge">Features</span>
            <h2>Everything Your Sales Floor Needs</h2>
            <p>One platform replaces a dozen tools. Every feature is designed for how furniture stores actually sell.</p>
          </div>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="feature-card">
                <div className="feature-icon" style={{ background: `${f.color}15`, color: f.color }}>
                  {f.icon}
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-highlight">
        <div className="landing-section-inner">
          <div className="highlight-grid">
            <div className="highlight-text">
              <span className="section-badge">Why Showroom AiPro</span>
              <h2>Your Sales Team, Supercharged</h2>
              <ul className="highlight-list">
                <li><Check size={18} /> AI writes personalized follow-ups in seconds, not minutes</li>
                <li><Check size={18} /> Customers see their room with your furniture before buying</li>
                <li><Check size={18} /> Social media posts and flyers generated instantly</li>
                <li><Check size={18} /> Sales training that actually adapts to each rep</li>
                <li><Check size={18} /> Language barriers eliminated with real-time translation</li>
                <li><Check size={18} /> Professional estimates sent from the chat window</li>
              </ul>
              <button className="highlight-cta" onClick={goToRegister}>
                Get Started Today <ArrowRight size={16} />
              </button>
            </div>
            <div className="highlight-visual">
              <div className="highlight-card hc-1">
                <Sparkles size={20} />
                <span>AI Follow-Up Generated</span>
                <p>"Hi Sarah, I wanted to follow up on the sectional you loved. We have 0% financing this weekend..."</p>
              </div>
              <div className="highlight-card hc-2">
                <Camera size={20} />
                <span>Room Visualization</span>
                <p>Customer's living room + your furniture = a sale they can see before they buy.</p>
              </div>
              <div className="highlight-card hc-3">
                <GraduationCap size={20} />
                <span>Training Score: A+</span>
                <p>"Excellent objection handling. You addressed price concerns with value-based responses."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-pricing" id="pricing">
        <div className="landing-section-inner">
          <div className="section-header">
            <span className="section-badge">Pricing</span>
            <h2>Simple, Transparent Pricing</h2>
            <p>One plan. Everything included. Your whole team gets access.</p>
          </div>
          <div className="pricing-card-wrapper">
            <div className="pricing-card">
              <div className="pricing-launch-ribbon">Launch Special</div>
              <div className="pricing-card-header">
                <h3>Showroom AiPro</h3>
                <p>Complete AI sales platform for your store</p>
              </div>
              <div className="pricing-price-row">
                <div className="pricing-original">
                  <span className="pricing-strike">$249</span>
                  <span className="pricing-strike-label">/month regular</span>
                </div>
                <div className="pricing-launch">
                  <span className="pricing-dollar">$</span>
                  <span className="pricing-amount">99</span>
                  <span className="pricing-period">/month</span>
                </div>
                <div className="pricing-save-badge">Save $150/mo</div>
              </div>
              <ul className="pricing-features">
                <li><Check size={16} /> All 10+ AI-powered features</li>
                <li><Check size={16} /> Unlimited team members</li>
                <li><Check size={16} /> AI Room Visualizer & Photo Rebuild</li>
                <li><Check size={16} /> Social Media Post & Flyer Generator</li>
                <li><Check size={16} /> Furniture Fred AI Sales Assistant</li>
                <li><Check size={16} /> Customer CRM & Follow-Up Autopilot</li>
                <li><Check size={16} /> Messages with Easy Estimate Builder</li>
                <li><Check size={16} /> Sales Training with AI Grading</li>
                <li><Check size={16} /> Real-Time Language Translator</li>
                <li><Check size={16} /> Team Management & Admin Controls</li>
                <li><Check size={16} /> No setup fees. Cancel anytime.</li>
              </ul>
              <button className="pricing-cta" onClick={goToRegister}>
                Start Your 7-Day Free Trial <ArrowRight size={18} />
              </button>
              <p className="pricing-note">7-day free trial · No credit card required</p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-faq" id="faq">
        <div className="landing-section-inner">
          <div className="section-header">
            <span className="section-badge">FAQ</span>
            <h2>Common Questions</h2>
          </div>
          <div className="faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? "open" : ""}`}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <ChevronDown size={20} />
                </button>
                {openFaq === i && <div className="faq-answer"><p>{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-final-cta">
        <div className="landing-section-inner">
          <div className="final-cta-content">
            <h2>Ready to Transform Your Sales Floor?</h2>
            <p>Join furniture stores already using AI to close more deals, follow up faster, and train smarter.</p>
            <div className="final-cta-row">
              <button className="final-cta-btn" onClick={goToRegister}>
                Get Started — $99/mo <ArrowRight size={18} />
              </button>
              <span className="final-cta-price-note">Launch price. Regular $249/mo.</span>
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <div className="landing-logo">
              <div className="landing-logo-icon">
                <Sparkles size={18} />
              </div>
              <span>Showroom <strong>AiPro</strong></span>
            </div>
            <p>AI-powered sales platform built for furniture retail.</p>
          </div>
          <div className="landing-footer-links">
            <div className="footer-link-col">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="footer-link-col">
              <h4>Access</h4>
              <a onClick={goToLogin} style={{ cursor: "pointer" }}>Store Login</a>
              <a onClick={goToRegister} style={{ cursor: "pointer" }}>Start 7-Day Free Trial</a>
            </div>
          </div>
          <div className="landing-footer-bottom">
            <p>&copy; {new Date().getFullYear()} Showroom AiPro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
